import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    asunto: '',
    mensaje: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre || !formData.email || !formData.mensaje) {
      toast.error('¡Por favor completa todos los campos obligatorios!');
      return;
    }

    try {
      toast.loading('Enviando mensaje...');
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contacto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('¡Mensaje enviado con éxito! Te responderé pronto.');
        setFormData({ nombre: '', email: '', asunto: '', mensaje: '' });
      } else {
        toast.error('Error al enviar el mensaje. Inténtalo de nuevo.');
      }
      
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      toast.error('Error al enviar el mensaje. Por favor, inténtalo de nuevo.');
    }
  };

  return (
    <section id="contacto" className="py-16 md:py-24 bg-[var(--bg-subtle)]">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="heading-1 mb-4">Contacto</h2>
          <p className="body-large max-w-2xl mx-auto">
            ¿Tienes alguna pregunta? Escríbeme y te responderé lo antes posible.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Google Maps */}
          <div className="mb-8 rounded-3xl overflow-hidden shadow-lg">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3041.8454977657356!2d-3.796891684253619!3d40.28549497937761!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x22d71e734e42413!2sHerbolario%20Tu%20Equilibrio%20Personal!5e0!3m2!1ses!2ses!4v1635789456789!5m2!1ses!2ses"
              width="100%" 
              height="300" 
              style={{ border: 0 }}
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Ubicación de Tu Equilibrio Personal"
              className="w-full"
            ></iframe>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="network-card">
                <CardHeader>
                  <CardTitle className="heading-3">Información de contacto</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="text-[var(--brand-dark)] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="body-medium font-semibold">Consulta Presencial</p>
                      <p className="body-small text-[var(--text-light)]">
                        Herbolario Tu Equilibrio Personal<br />
                        Calle Murcia 1, Fuenlabrada, Madrid
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="text-[var(--brand-dark)] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="body-medium font-semibold">Escuela Kabat</p>
                      <p className="body-small text-[var(--text-light)]">Madrid, España</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Mail className="text-[var(--brand-dark)] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="body-medium font-semibold">Email</p>
                      <a href="mailto:reparatucuerpo.rbf@gmail.com" className="link-text">
                        reparatucuerpo.rbf@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Phone className="text-[var(--brand-dark)] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="body-medium font-semibold">Teléfono / WhatsApp</p>
                      <a href="tel:+34742030476" className="link-text">
                        +34 742 03 04 76
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="network-card bg-[var(--brand-dark)] text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="text-[var(--brand-primary)]" size={24} />
                    <h3 className="heading-3 text-white">Horario de atención</h3>
                  </div>
                  <div className="space-y-2 text-white opacity-90">
                    <p className="body-medium">
                      <span className="font-semibold">Lunes a Viernes:</span> 10:00 - 14:00 y 16:00 - 20:00
                    </p>
                    <p className="body-medium">
                      <span className="font-semibold">Sábados y Domingos:</span> Cerrado
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="network-card">
              <CardHeader>
                <CardTitle className="heading-3">Enviar mensaje</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="nombre">Nombre *</Label>
                    <Input
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      placeholder="Tu nombre"
                      className="mt-1"
                      required
                      data-testid="contact-nombre"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="tu@email.com"
                      className="mt-1"
                      required
                      data-testid="contact-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="asunto">Asunto</Label>
                    <Input
                      id="asunto"
                      name="asunto"
                      value={formData.asunto}
                      onChange={handleInputChange}
                      placeholder="Asunto de tu mensaje"
                      className="mt-1"
                      data-testid="contact-asunto"
                    />
                  </div>

                  <div>
                    <Label htmlFor="mensaje">Mensaje *</Label>
                    <Textarea
                      id="mensaje"
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleInputChange}
                      placeholder="Escribe tu mensaje aquí..."
                      className="mt-1"
                      rows={5}
                      required
                      data-testid="contact-mensaje"
                    />
                  </div>

                  <Button type="submit" className="btn-primary w-full" size="lg" data-testid="contact-submit">
                    <Send className="mr-2 h-4 w-4" />
                    Enviar Mensaje
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
